'use client';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  Settings,
  BookOpen,
  BarChart3,
  Clock,
  User,
  Archive,
} from 'lucide-react';

interface SidebarItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

const sidebarItems: SidebarItem[] = [
  {
    href: '/',
    label: 'Dashboard',
    icon: BarChart3,
    description: 'Overview of your learning progress',
  },
  {
    href: '/account',
    label: 'Account Settings',
    icon: User,
    description: 'Manage your profile and account',
  },
  {
    href: '/preferences',
    label: 'Learning Preferences',
    icon: Settings,
    description: 'Customize your learning experience',
  },
  {
    href: '/words',
    label: 'My Words',
    icon: BookOpen,
    description: 'View and manage learned words',
  },
  // {
  //   href: '/dashboard/schedule',
  //   label: 'Learning Schedule',
  //   icon: Calendar,
  //   description: 'Set your learning schedule'
  // },
  // {
  //   href: '/dashboard/goals',
  //   label: 'Learning Goals',
  //   icon: Target,
  //   description: 'Track your learning goals'
  // },
  {
    href: '/progress',
    label: 'Progress History',
    icon: Clock,
    description: 'View your learning history',
  },
  {
    href: '/revision',
    label: 'Revision',
    icon: Archive,
    description: 'Revisit your past collections',
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DashboardSidebar({ isOpen, onClose }: SidebarProps) {
  const pathname = useLocation();

  // Helper function to determine if a link is active
  const isLinkActive = (href: string) => {
    if (href === '/') {
      // For dashboard home, only match exact path
      return pathname.pathname === '/';
    }
    // For other pages, check if pathname starts with the href
    return pathname.pathname.startsWith(href);
  };

  return (
    <>
      {isOpen && (
        <div
          className='fixed inset-0 z-20 bg-black/50 md:hidden'
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 md:relative md:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <nav className='h-full p-4 pt-24 space-y-2 overflow-y-auto md:pt-4'>
          {sidebarItems.map(item => {
            const Icon = item.icon;
            const isActive = isLinkActive(item.href);

            return (
              <Link
                key={item.href}
                to={item.href}
                className={`group flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                  isActive
                    ? 'bg-brand-teal text-brand-navy font-medium'
                    : 'text-gray-700 hover:bg-brand-yellow/10 hover:text-brand-navy'
                }`}
              >
                <Icon
                  className={`w-5 h-5 ${
                    isActive
                      ? 'text-brand-navy'
                      : 'text-gray-500 group-hover:text-brand-navy'
                  }`}
                />
                <div className='flex-1'>
                  <div className='font-medium'>{item.label}</div>
                  {item.description && (
                    <div className='text-xs text-gray-500 group-hover:text-gray-600'>
                      {item.description}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
